/**
 * @fileoverview Care for a plant: the body the card's Care tray (CareStrip) draws inline under its strip, and the sheet that
 * wraps the same body in a bottom panel titled Care (B136: the body is lifted into PlantAboutBody so both draw one thing).
 *
 * Under the title (the plant's one name) its kind as a name ("Romaine
 * Lettuce"; the backend's names.kindName, services/grow/plantNaming), which
 * the name leaves out, then three sections, in the words the week's grid
 * and items use:
 *
 * - Temperature: the plant's scale, hottest first. Too hot, grows slowly,
 *   grows best (give or take a few degrees), grows slowly, and then what
 *   harms it: nights under the chilling line for a warm or heat-loving
 *   crop, frost and the frost that kills it for a tender one, or, for a
 *   hardy one, how cold it survives.
 * - Water: what it needs a week, the most rain a week it can take, and the
 *   rain in a day that is a downpour, then a few notes from its water data
 *   (standing water, a container, mulch, deep watering).
 * - Feeding: the plant group's feeding, in the printed planner's sentence.
 *
 * The scales are the server's, rows and words (GET /plant-about's scale,
 * services/plants/aboutScale; audit batch 4, A27): the range the week is
 * graded by, the grading's chilling line and frost from the Grow settings,
 * and the water limits the week is judged by (the outlook's, else the
 * crop's water data), so the sheet and the week agree whatever the owner
 * sets, and before the outlook lands. The app draws each row's swatch from
 * its verdict colour and look, and the section headings.
 *
 * Where: the plant card's Care tray (CareStrip, PlantAboutBody); the sheet (PlantAboutSheet) is the same body in a panel.
 * API: GET /plant-about/:plantObjId?lang=&unit= (getPlantAbout, only while
 * open, kept ABOUT_STALE_MS).
 * Settings: the gardener's temperature unit (°C also means millimetres).
 * Words (en/es): plants.about.* (title, temperature, water.heading, feeding,
 * heavyFeeder); plants.card.care (the sheet's title); the rows and notes are the server's.
 *
 * @module PlantAboutSheet
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useUserConfig } from '@/shared/context/userConfigStore';
import { getPlantAbout, type AboutScaleRow } from '@/shared/api/plantAbout';
import { TOKEN_FILL, TOKEN_RING, TOKEN_TINT } from '@/shared/theme/verdictTokens';

/** How long a plant's About answer is kept before it is read again when the sheet opens: the sheet's own timing (10 minutes). */
const ABOUT_STALE_MS = 10 * 60_000;

/** What the body is about, and whether it reads its answer yet. */
interface PlantAboutBodyProps {
  /** The plant. */
  plantObjId: number;
  /** Its one name ("Devil's Ear Lettuce"), which the kind line leaves out when it is the same. */
  plantName: string;
  /** Its kind as a name ("Romaine Lettuce"; the backend's names.kindName), said first; null for a plant that is only its crop. */
  kindName?: string | null;
  /** Whether to read the answer (the tray or the sheet is open). */
  enabled: boolean;
}

/** What the sheet is about, and whether it is open. */
interface PlantAboutSheetProps {
  /** The plant. */
  plantObjId: number;
  /** Its one name ("Devil's Ear Lettuce"), for the title. */
  plantName: string;
  /** Its kind as a name ("Romaine Lettuce"; the backend's names.kindName), said under the title; null for a plant that is only its crop. */
  kindName?: string | null;
  /** Whether the sheet is up. */
  open: boolean;
  /** Called as it opens or closes. */
  onOpenChange: (open: boolean) => void;
}

/**
 * A row's swatch classes: its verdict colour filled, tinted, or tinted with a ring, as the grid draws the same meaning.
 *
 * @param swatch - The server's token and look.
 */
const swatchOf = (swatch: AboutScaleRow['swatch']): string => {
  if (swatch.look === 'fill') return TOKEN_FILL[swatch.token];
  if (swatch.look === 'ring') return cn(TOKEN_TINT[swatch.token], 'ring-2 ring-inset', TOKEN_RING[swatch.token]);
  return TOKEN_TINT[swatch.token];
};

/**
 * The plant's temperature scale, water and feeding, as a block to draw anywhere (the Care tray, the sheet).
 *
 * @param props - PlantAboutBodyProps.
 */
export const PlantAboutBody: React.FC<PlantAboutBodyProps> = ({ plantObjId, plantName, kindName = null, enabled }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { userConfig } = useUserConfig();
  const unit = userConfig.preferences.temperatureUnit === 'C' ? 'C' : 'F';
  const { data: about } = useQuery({
    queryKey: ['plant-about', plantObjId, language, unit],
    queryFn: () => getPlantAbout(plantObjId, language, unit),
    enabled,
    staleTime: ABOUT_STALE_MS,
  });

  const scale = about?.scale ?? null;
  const tempRows = scale?.temperature ?? [];
  const waterRows = scale?.water ?? [];
  const waterNotes = scale?.waterNotes ?? '';
  const classWords = scale?.classWords ?? [];
  const feeding = about?.feeding ?? null;

  /** A scale: a swatch, the readings and what they mean, a line each. */
  const drawScale = (rows: AboutScaleRow[]) => (
    <ul className="flex flex-col gap-0.5 text-sm">
      {rows.map((row) => (
        <li
          key={row.key}
          className={cn('grid grid-cols-[0.875rem_8.5rem_minmax(0,1fr)] items-center gap-2.5 rounded px-1.5 py-1 tabular-nums', row.best && TOKEN_TINT.good)}
        >
          <span className={cn('h-3.5 w-3.5 rounded-[3px]', swatchOf(row.swatch))} aria-hidden="true" />
          <span className="font-semibold">{row.reading}</span>
          <span className={row.best ? undefined : 'text-muted-foreground'}>{row.meaning}</span>
        </li>
      ))}
    </ul>
  );
  /** A section's heading: small capitals over a line. */
  const heading = (text: string) => (
    <h3 className="font-body text-xs font-semibold uppercase tracking-wider text-muted-foreground">{text}</h3>
  );

  return (
    <div className="flex flex-col gap-3.5 font-sans">
      {/* The kind, which the name leaves out ("Romaine Lettuce" under "Devil's Ear Lettuce"), lives here. */}
      {kindName && kindName !== plantName && <p className="text-sm text-muted-foreground">{kindName}</p>}
      {tempRows.length > 0 && (
        <section className="flex flex-col gap-1.5 border-t pt-2.5">
          {heading(classWords.length > 0 ? `${t('plants.about.temperature')} · ${classWords.join(', ')}` : t('plants.about.temperature'))}
          {drawScale(tempRows)}
        </section>
      )}
      {(waterRows.length > 0 || waterNotes) && (
        <section className="flex flex-col gap-1.5 border-t pt-2.5">
          {heading(t('plants.about.water.heading'))}
          {waterRows.length > 0 && drawScale(waterRows)}
          {waterNotes && <p className="text-sm">{waterNotes}</p>}
        </section>
      )}
      {feeding && (
        <section className="flex flex-col gap-1.5 border-t pt-2.5">
          {heading(feeding.heavyFeeder ? `${t('plants.about.feeding')} · ${t('plants.about.heavyFeeder')}` : t('plants.about.feeding'))}
          <p className="text-sm">{feeding.sentence}</p>
        </section>
      )}
    </div>
  );
};

/**
 * The same body in a bottom panel titled Care.
 *
 * @param props - PlantAboutSheetProps.
 */
export const PlantAboutSheet: React.FC<PlantAboutSheetProps> = ({ plantObjId, plantName, kindName = null, open, onOpenChange }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <BottomPanel open={open} onOpenChange={onOpenChange} title={`${t('plants.card.care')} · ${plantName}`} wrapTitle>
      <PlantAboutBody plantObjId={plantObjId} plantName={plantName} kindName={kindName} enabled={open} />
    </BottomPanel>
  );
};
